import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import _ from 'lodash';
import {Container, Stack, Typography} from '@mui/material';
import {useBusyCallback, useLiveRef} from '../util/react_hooks.js';
import api from '../util/api.js';
import {qw} from '../util.js';
import {run as runP5Shuffle} from './p5shuffle.js';
import {useLoader} from './Loader.js';
import SCButton from './SCButton.js';
import SCTextField from './SCTextField.js';
import SCCheckbox from './SCCheckbox.js';
import ConsentCheckbox from './ConsentCheckbox.js';

const absoluteContainerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    height: '80%',
    background: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 8,
};

const absoluteShuffleStyle = {
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 9999,
    width: '100vw',
    height: '100vh',
};

const ErrorPage = props=>{
    let {error, errorRefresh, absolute} = props;
    let refresh = useCallback(()=>{
        if (errorRefresh)
            return void errorRefresh();
        window.location.reload();
    }, [errorRefresh]);
    let msg = useMemo(()=>{
        if (error?.code=='student_not_found')
            return 'אופס, נראה שהקישור שגוי...';
        return 'אופס, משהו השתבש...';
    }, [error]);
    return <Stack justifyContent="center" alignItems="center" 
      sx={absolute ? absoluteContainerStyle : null}>
      <Typography variant="h3">{msg}</Typography>
      <Typography>לפרטים, צרו קשר עם הקורא בקלפים ממנו קיבלתם את הקישור</Typography>
      <SCButton text="ריענון" onClick={refresh}/>
    </Stack>;
};

const SetupPage = props=>{
    let {customerEmail, setCustomerEmail, customerName, setCustomerName,
        setPage, showError, validId, absolute, id, description, student,
        hideName} = props;
    let [validationErrors, setValidationErrors] = useState({});
    let [agreeToTerms, setAgreeToTerms] = useState(false);
    let [consent, setConsent] = useState(false);
    let [onConfirm, confirming] = useBusyCallback(async ()=>{
        let studentName = student?.username||'הקורא בקלפים';
        try {
            await api.tarotmail.checkEligibility(id, customerName, customerEmail);
            setPage(PAGES.SHUFFLE);
        } catch (e) {
            if (e.code=='customer_not_eligible')
                return void setValidationErrors({email: 'כבר השתמשת באפליקציית המסרים, ניתן לבקש עוד סבב מ'+studentName});
            if (e.code!='validation_error')
                return void showError(e);
            let vErrors = {};
            e.extra.forEach(c=>{
                let k = c.path[0];
                let v = {
                    name: 'שם',
                    email: 'דוא"ל',
                }[k];
                if (v)
                    vErrors[k] = `נא למלא ${v} תקין`;
            });
            setValidationErrors(vErrors);
        }
    }, [id, customerEmail, customerName, student]);
    return <Container maxWidth="lg">
      <Stack justifyContent="center" alignItems="center"
        sx={absolute ? absoluteContainerStyle : null}>
        <Stack dir="rtl" direction="column" gap={2} alignItems="center" justifyContent="center">
          <Typography variant="h3" align="center">מסר שבועי במייל</Typography>
          {student?.username&&!hideName&&<Typography variant="h5" align="center">{student.username}</Typography>}
          {description&&<Container maxWidth="lg">{description}</Container>}
          <Stack direction="column" gap={2} alignItems="center" justifyContent="center" maxWidth={600}>
            <SCTextField required label="שם" variant="outlined" value={customerName} onChange={setCustomerName}
              error={!!validationErrors.name} helperText={validationErrors.name}/>
            <SCTextField required label="מייל" variant="outlined" value={customerEmail} onChange={setCustomerEmail}
              error={!!validationErrors.email} helperText={validationErrors.email}/>
            <SCCheckbox required value={agreeToTerms} onChange={setAgreeToTerms}
              label={`אני מאשר/ת לקבל מסרים יומיים, השראה, עדכונים ותוכן שיווקי מ${student?.username||''} למייל שלי. ידוע לי שאוכל להסיר את עצמי מהרשימה בכל עת, בלחיצת כפתור.`}/>
            <ConsentCheckbox value={consent} onChange={setConsent}/>
            <SCButton text="אני רוצה מסר" variant="outlined" onClick={onConfirm} size="large"
              disabled={!agreeToTerms||!validId||!consent} loading={confirming}/>            
          </Stack>
        </Stack>
      </Stack>
    </Container>;
};

const ShufflePage = props=>{
    let {customerName, customerEmail, absolute, id} = props;
    let [startLoading] = useLoader();
    let [p5Node, setP5Node] = useState();
    let onDeck = useCallback(async deck=>{
        await api.tarotmail.subscribe(id, {
            name: customerName,
            email: customerEmail,
            cards: deck,
        });
    }, [id, customerName, customerEmail]);
    let onDeckRef = useLiveRef(onDeck);
    let startLoadingRef = useLiveRef(startLoading);
    useEffect(()=>{
        if (!p5Node)
            return;
        let handler = runP5Shuffle(p5Node, {
            onDeck: deck=>onDeckRef.current(deck),
            startLoading: ()=>startLoadingRef.current(),
        });
        return ()=>handler?.stop();
    }, [p5Node]);
    return <>
      <div ref={setP5Node} style={absolute ? absoluteShuffleStyle : null}></div>
    </>;
};

const PAGES = _.keyBy(qw`SETUP SHUFFLE ERROR`);

const COMPS = {
    [PAGES.SETUP]: SetupPage,
    [PAGES.SHUFFLE]: ShufflePage,
    [PAGES.ERROR]: ErrorPage,
};

const useStudent = id=>{
    let {data} = useQuery({
        queryKey: ['validate_student', id],
        queryFn: async ()=>{
            try { return await api.tarotmail.validateStudent(id); }
            catch (e) { return e; }
        },
        retry: false,
        enabled: !!id,
    });
    return data;
};

const TarotmailCustomerComp = props=>{
    let {id, absolute, description, hideName} = props;
    let [page, setPage] = useState(PAGES.SETUP);
    let [customerEmail, setCustomerEmail] = useState('');
    let [customerName, setCustomerName] = useState('');
    let [error, setError] = useState();
    let [errorRefresh, setErrorRefresh] = useState();
    let showError = useCallback((e, opt={})=>{
        let {refresh} = opt;
        setError(e);
        setErrorRefresh(refresh);
        setPage(PAGES.ERROR);
    }, []);
    let student = useStudent(id)
    useEffect(()=>{
        if (student&&!student.ok)
            showError(student);
    }, [student]);
    if (!id)
        return null;
    let Comp = COMPS[page];
    return <Comp
      page={page}
      setPage={setPage}
      customerEmail={customerEmail}
      setCustomerEmail={setCustomerEmail}
      customerName={customerName}
      setCustomerName={setCustomerName}
      error={error}
      showError={showError}
      errorRefresh={errorRefresh}
      validId={!!student?.ok}
      student={student}
      absolute={absolute}
      id={id}
      description={description}
      hideName={hideName}
    />;
};

export default TarotmailCustomerComp;
